// Badge / Status Pill — pequeño chip con tono semántico opcional.
//
// Patrón visual del mockup (XStatusPill en crud-shared.jsx):
//   - dot + label inline
//   - 0.5px border + bg soft del tono
//   - 4px radius, 11px text, uppercase tracking

import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-overlay text-fg-secondary border border-border',
        ok: 'bg-ok-soft text-ok border border-ok/30',
        warn: 'bg-warn-soft text-warn border border-warn/30',
        crit: 'bg-crit-soft text-crit border border-crit/30',
        info: 'bg-info/10 text-info border border-info/30',
        accent: 'bg-accent text-accent-ink border border-accent',
      },
      dot: {
        true: '',
        false: '',
      },
    },
    defaultVariants: { tone: 'neutral', dot: false },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, tone, dot, children, ...props },
  ref,
) {
  return (
    <span ref={ref} className={cn(badgeVariants({ tone, dot }), className)} {...props}>
      {dot ? (
        <span
          aria-hidden
          className="inline-block size-1.5 rounded-full bg-current"
          style={{ flexShrink: 0 }}
        />
      ) : null}
      {children}
    </span>
  );
});
